import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/solid";
import { cn } from "@utils";

// Palette + default glyph per tone — the tinted bars used across the app (the Batch
// Template "Issues found" step, the calendar cancel confirmation, …). Text stays zinc so
// the message reads as normal copy against the tint.
const TONES = {
  error: { border: "border-red-200", bg: "bg-red-50", icon: "text-red-600", Icon: ExclamationTriangleIcon },
  warning: { border: "border-amber-200", bg: "bg-amber-50", icon: "text-amber-600", Icon: ExclamationTriangleIcon },
  info: { border: "border-zinc-200", bg: "bg-zinc-50", icon: "text-zinc-400", Icon: InformationCircleIcon },
  success: { border: "border-green-200", bg: "bg-green-50", icon: "text-green-600", Icon: CheckCircleIcon },
};

/**
 * A coloured notice bar: a tinted, rounded box with a leading icon and a message. Used
 * flat as a warning/alert, or as the header of a collapsible section — pass `trailing` for
 * a right-aligned control and `children` for the body beneath the header (see CollapsibleBar).
 *
 * Colour comes from `tone`; `borderColor`/`bgColor` override it when a caller needs an
 * explicit pairing. When `onHeaderClick` is set the icon + message become a button (spread
 * `headerButtonProps` onto it, e.g. aria-expanded/aria-controls); `trailing` sits beside it.
 */
export default function Banner({
  tone = "info",
  icon,
  message,
  trailing,
  onHeaderClick,
  headerButtonProps,
  className,
  headerClassName,
  textClassName = "text-zinc-700",
  borderColor,
  bgColor,
  children,
}) {
  const t = TONES[tone] ?? TONES.info;
  const Glyph = t.Icon;

  // icon + message. When the header is clickable this is wrapped in the toggle button;
  // `trailing` stays a sibling of that button so it can itself be an interactive control
  // (nesting a button inside the header button would be invalid markup).
  const content = (
    <>
      {icon ?? <Glyph className={cn("size-4 shrink-0", t.icon)} />}
      <span className={cn("flex-1 text-left text-sm", textClassName)}>{message}</span>
    </>
  );

  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-lg border",
        borderColor ?? t.border,
        bgColor ?? t.bg,
        className,
      )}
    >
      <div className={cn("flex shrink-0 items-center gap-3 px-4 py-2", headerClassName)}>
        {onHeaderClick ? (
          <button
            type="button"
            onClick={onHeaderClick}
            className="flex min-w-0 flex-1 items-center gap-3 text-left"
            {...headerButtonProps}
          >
            {content}
          </button>
        ) : (
          content
        )}
        {trailing}
      </div>
      {children}
    </div>
  );
}
